/**
 * The embedded self-test (the `lavendla/self-test` block) runs in an iframe
 * from the app, so this page's Google Tag Manager cannot see it. The embed
 * script re-dispatches the frame's events as `lavendla:self-test` on the
 * block's container; this sends them on to `dataLayer`.
 *
 * The event names and `self_test_title` are the ones the Gravity Forms forms
 * pushed (see the digital-contracts store), so the GA4 tags and reports built
 * on them keep working. `self_test_id` is now the app's test slug rather than
 * a form number. There is no score: for tests such as Depression it is health
 * data, which does not belong in analytics.
 */

const DATA_LAYER_EVENTS: Record<string, string> = {
  completed: "self_test_completed",
  login_clicked: "self_test_login_clicked",
  started: "self_test_started",
};

interface SelfTestEventDetail {
  event?: string;
  slug?: string;
}

export function pushSelfTestEvent(
  detail: SelfTestEventDetail,
  container: HTMLElement | null
): void {
  const event = detail.event ? DATA_LAYER_EVENTS[detail.event] : undefined;
  // `ready`, `resize` and the like only concern the frame itself.
  if (!event) {
    return;
  }

  const slug = detail.slug || container?.dataset.lavendlaSelfTest || "";
  const pageTitle = container?.dataset.pageTitle || "";

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    self_test_id: slug,
    self_test_title: `Terapi / Självtest - ${pageTitle}`,
  });
}

export function initSelfTestAnalytics(root: Document = document): void {
  root.addEventListener("lavendla:self-test", (e) => {
    const target = e.target instanceof HTMLElement ? e.target : null;
    pushSelfTestEvent(
      (e as CustomEvent<SelfTestEventDetail>).detail || {},
      target?.closest<HTMLElement>("[data-lavendla-self-test]") ?? null
    );
  });
}

const BOOKING_CLICK_SESSION_KEY = "lavendla_self_test_booking_click";

/**
 * The self-test result in the app links here with
 * `?from=self-test&self_test=<slug>`: "Boka samtal" to the therapist list, and
 * a level's own button (ADHD's "Gratis konsultation", to the assessment page)
 * with `self_test_cta=consultation` as well. The app has no analytics of its
 * own, so the click is reported on arrival, once per test, button and browser
 * session (a reload of the same URL is not a second click). The consultation
 * click keeps the event name GTM used for it before the migration.
 */
export function reportBookingArrival(
  search: string = window.location.search
): void {
  const params = new URLSearchParams(search);
  if (params.get("from") !== "self-test") {
    return;
  }

  const slug = params.get("self_test") || "";
  const event =
    params.get("self_test_cta") === "consultation"
      ? "self_test_consultation_click"
      : "self_test_booking_click";
  const sessionKey = `${BOOKING_CLICK_SESSION_KEY}:${event}:${slug}`;
  try {
    if (window.sessionStorage.getItem(sessionKey)) {
      return;
    }
    window.sessionStorage.setItem(sessionKey, "1");
  } catch {
    // Storage blocked: report it anyway; a reload may count twice.
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    self_test_id: slug,
  });
}

initSelfTestAnalytics();
reportBookingArrival();
